// frontend/src/services/clubsApi.js
// Working + transition-safe:
// - Works whether REACT_APP_API_BASE is "" (CRA proxy → setupProxy.js / PROXY_API_TARGET) OR absolute API URL
// - Supports BOTH "/api/..." and "/arthouse/..." style paths safely
// - Prevents "/api/api/..." double-prefix bug
// - Keeps your dev header + optional bearer header behavior

const API_BASE = process.env.REACT_APP_API_BASE || "";

// Your backend namespace (current)
const ARTHOUSE_BASE = "/api/arthouse";
const CLUBS_BASE = `${ARTHOUSE_BASE}/clubs`;

// R2 upload route (mounted in server.js as app.use("/api/upload", ...))
const CLUB_UPLOAD_R2 = "/api/upload/clubs/image";
const CLUB_FEED_UPLOAD_R2 = "/api/upload/clubs/feed-media";

// must match arthouseApi.js
function getDevUserIdHeader() {
  if (process.env.NODE_ENV !== "development") return {};
  const id = localStorage.getItem("DEV_USER_ID");
  return id ? { "x-user-id": id } : {};
}

// optional, matches arthouseApi.js
function getAuthHeader() {
  const keys = ["accessToken", "token", "jwt", "authToken"];

  let token = null;
  try {
    for (const k of keys) {
      token = localStorage.getItem(k);
      if (token) break;
    }
    if (!token) {
      for (const k of keys) {
        token = sessionStorage.getItem(k);
        if (token) break;
      }
    }
  } catch {
    // ignore
  }

  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Transition normalizer (HARDENED):
 * - absolute URLs: untouched
 * - "/api/..." stays
 * - "/api/api/..." collapses to "/api/..." (fixes your bug)
 * - "/arthouse/..." becomes "/api/arthouse/..."
 * - anything else "/x" stays as-is
 */
function normalizePath(path) {
  const raw = String(path || "");

  // absolute URLs: don't touch
  if (/^https?:\/\//i.test(raw)) return raw;

  // ensure leading slash
  const p = raw.startsWith("/") ? raw : `/${raw}`;

  // fix the exact bad path: "/api/api/..."
  if (p.startsWith("/api/api/")) return p.replace(/^\/api\/api\//, "/api/");

  // already includes /api
  if (p.startsWith("/api/")) return p;

  // allow arthouse shorthand (future-proof)
  if (p.startsWith("/arthouse/")) return `/api${p}`;

  // default: leave as-is
  return p;
}

/**
 * Detect FormData reliably. Relying only on `instanceof FormData` breaks when:
 * - another realm / polyfill supplies a different FormData constructor
 * - tests or older environments mismatch prototype
 * Wrong detection causes us to set Content-Type: application/json while the body is
 * still multipart — Express then never runs multer and club comments 400.
 */
function isFormDataBody(body) {
  if (body == null || typeof body !== "object") return false;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  return typeof body.append === "function" && typeof body.get === "function";
}

export function isLikelyFileOrBlob(x) {
  if (x == null || typeof x !== "object") return false;
  if (typeof Blob !== "undefined" && x instanceof Blob) return true;
  if (typeof File !== "undefined" && x instanceof File) return true;
  return (
    typeof x.size === "number" &&
    x.size >= 0 &&
    (typeof x.type === "string" || x.type === "") &&
    typeof x.arrayBuffer === "function"
  );
}

async function apiFetch(path, options = {}) {
  const normalized = normalizePath(path);
  const url = /^https?:\/\//i.test(normalized) ? normalized : `${API_BASE}${normalized}`;

  const method = (options.method || "GET").toUpperCase();
  const hasBody = options.body != null;
  const isFormData = isFormDataBody(options.body);

  const headers = {
    ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
    ...getDevUserIdHeader(),
    ...getAuthHeader(),
  };

  // Never send a manual Content-Type for FormData — the browser must set the boundary.
  if (isFormData) {
    delete headers["Content-Type"];
    delete headers["content-type"];
  }

  const res = await fetch(url, {
    credentials: "include",
    ...options,
    method,
    headers,
  });

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const msg =
      typeof data === "string"
        ? data
        : data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    err.path = normalized;
    throw err;
  }

  return data;
}

/* -----------------------------
   Clubs
------------------------------ */

export function archiveClub(slug, isDeleted) {
  if (!slug) throw new Error("Missing slug");

  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/archive`, {
    method: "PATCH",
    body: JSON.stringify({ is_deleted: !!isDeleted }),
  });
}

export async function fetchClubs({ search = "", page = 1, pageSize = 12 } = {}) {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));

  const data = await apiFetch(`${CLUBS_BASE}?${qs.toString()}`);

  if (Array.isArray(data?.items)) return data;
  if (Array.isArray(data?.clubs)) return { items: data.clubs };
  if (Array.isArray(data)) return { items: data };
  return { items: [] };
}

export function fetchClub(slug) {
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}`);
}

export function fetchClubArtworks(slug, { page = 1, pageSize = 18 } = {}) {
  const qs = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });

  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/artworks?${qs.toString()}`);
}

export function createClub(payload) {
  return apiFetch(`${CLUBS_BASE}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function joinClub(slug) {
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/join`, {
    method: "POST",
  });
}

export function leaveClub(slug) {
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/leave`, {
    method: "POST",
  });
}

export function addArtworkToClub(slug, { artworkId, note = "" } = {}) {
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/artworks`, {
    method: "POST",
    body: JSON.stringify({ artworkId, note }),
  });
}

export function fetchMyClubs() {
  return apiFetch(`${CLUBS_BASE}/me`);
}

export function fetchArtworkClubs(artworkId) {
  return apiFetch(`${CLUBS_BASE}/artworks/${encodeURIComponent(String(artworkId))}/clubs`);
}

/* -----------------------------
   Club Images (R2)
------------------------------ */

/**
 * Upload a club image file to Cloudflare R2.
 * Backend route: POST /api/upload/clubs/image
 *
 * Expects form-data:
 * - image (file)
 * - slug (string)
 * - imageType ("avatar" | "banner")
 *
 * Returns: { message, imageType, image_url, key }
 */
export async function uploadClubImage({ slug, file, imageType = "avatar" }) {
  if (!slug) throw new Error("Missing slug");
  if (!file) throw new Error("Missing file");

  const fd = new FormData();
  fd.append("image", file);
  fd.append("slug", slug);
  fd.append("imageType", imageType);

  return apiFetch(`${CLUB_UPLOAD_R2}`, {
    method: "POST",
    body: fd,
  });
}

/**
 * Persist the avatar/banner URL to the club record (DB) so it shows for everyone.
 * Backend route: PUT /api/arthouse/clubs/:slug/images
 */
export function updateClubImages(slug, { avatar_image_url, banner_image_url } = {}) {
  if (!slug) throw new Error("Missing slug");

  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/images`, {
    method: "PUT",
    body: JSON.stringify({
      avatar_image_url: avatar_image_url ?? null,
      banner_image_url: banner_image_url ?? null,
    }),
  });
}

/* -----------------------------
   Club feed (numeric club id in path)
------------------------------ */

export function fetchClubPosts(clubId, { limit = 20, offset = 0 } = {}) {
  const id = Number(clubId);
  if (!Number.isFinite(id)) throw new Error("Invalid club id");

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  return apiFetch(`${CLUBS_BASE}/${id}/posts?${qs.toString()}`);
}

/** Full feed-shaped post row for thread deep links (includes club_id, comment_count). */
export function fetchClubPostById(postId) {
  const id = Number(postId);
  if (!Number.isFinite(id)) throw new Error("Invalid post id");
  return apiFetch(`${CLUBS_BASE}/posts/${id}`);
}

/** Canonical shareable URL for a club feed thread (full page route). */
export function buildClubPostShareUrl(clubSlug, postId) {
  if (!clubSlug || postId == null) return "";
  const origin =
    typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
  if (!origin) return "";
  return `${origin}/clubs/${encodeURIComponent(String(clubSlug))}/posts/${encodeURIComponent(String(postId))}`;
}

export function createClubPost(clubId, { content, image_url, artwork_id, attachments } = {}) {
  const id = Number(clubId);
  if (!Number.isFinite(id)) throw new Error("Invalid club id");

  return apiFetch(`${CLUBS_BASE}/${id}/posts`, {
    method: "POST",
    body: JSON.stringify({
      content: content ?? "",
      image_url: image_url ?? null,
      artwork_id: artwork_id ?? null,
      attachments: Array.isArray(attachments) ? attachments : [],
    }),
  });
}

export async function uploadClubFeedMedia({ clubId, file }) {
  const id = Number(clubId);
  if (!Number.isFinite(id)) throw new Error("Invalid club id");
  if (!file) throw new Error("Missing file");

  const fd = new FormData();
  fd.append("file", file);
  fd.append("clubId", String(id));

  return apiFetch(`${CLUB_FEED_UPLOAD_R2}`, {
    method: "POST",
    body: fd,
  });
}

export function toggleClubPostLike(postId) {
  const id = Number(postId);
  if (!Number.isFinite(id)) throw new Error("Invalid post id");

  return apiFetch(`${CLUBS_BASE}/posts/${id}/like`, {
    method: "POST",
  });
}

export function fetchClubPostComments(
  postId,
  { limit = 40, offset = 0, sort = "new", subtreeBatchLimit, listContinuation } = {}
) {
  const id = Number(postId);
  if (!Number.isFinite(id)) throw new Error("Invalid post id");

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    sort: String(sort || "new").toLowerCase(),
  });
  const sb = Number(subtreeBatchLimit);
  if (Number.isFinite(sb)) {
    qs.set("subtreeBatchLimit", String(Math.trunc(sb)));
  }
  if (listContinuation != null && String(listContinuation).trim() !== "") {
    qs.set("list_continuation", String(listContinuation).trim());
  }

  return apiFetch(`${CLUBS_BASE}/posts/${id}/comments?${qs.toString()}`).then((data) => {
    if (Array.isArray(data)) {
      return {
        comments: data,
        subtree_truncated: false,
        returned_comment_count: data.length,
        next_root_offset: undefined,
        truncation_reason: null,
        list_continuation: null,
      };
    }
    const comments = Array.isArray(data?.comments) ? data.comments : [];
    const nextRaw = data?.next_root_offset;
    const nextNum = Number(nextRaw);
    return {
      comments,
      subtree_truncated: Boolean(data?.subtree_truncated),
      returned_comment_count: Number(data?.returned_comment_count) || comments.length,
      next_root_offset: Number.isFinite(nextNum) ? nextNum : undefined,
      truncation_reason: data?.truncation_reason ?? null,
      list_continuation: data?.list_continuation ?? null,
    };
  });
}

/**
 * Full-post comment search (all comments for the post, not limited to loaded pages).
 * Resolves to { matches, search_truncated, returned_match_count, search_limit } (normalized; legacy array wrapped).
 * @param {number} postId
 * @param {{ q?: string, limit?: number, signal?: AbortSignal }} [opts]
 */
export function fetchClubPostCommentsSearch(postId, { q = "", limit = 30, signal } = {}) {
  const id = Number(postId);
  if (!Number.isFinite(id)) throw new Error("Invalid post id");

  const qs = new URLSearchParams();
  qs.set("q", String(q).trim());
  const lim = Number(limit);
  const safeLim = Number.isFinite(lim) ? Math.min(50, Math.max(1, Math.trunc(lim))) : 30;
  qs.set("limit", String(safeLim));

  return apiFetch(`${CLUBS_BASE}/posts/${id}/comments/search?${qs.toString()}`, { signal }).then(
    (data) => {
      if (Array.isArray(data)) {
        return {
          matches: data,
          search_truncated: false,
          returned_match_count: data.length,
          search_limit: safeLim,
        };
      }
      const matches = Array.isArray(data?.matches) ? data.matches : [];
      return {
        matches,
        search_truncated: Boolean(data?.search_truncated),
        returned_match_count: Number(data?.returned_match_count) || matches.length,
        search_limit: Number(data?.search_limit) || safeLim,
      };
    }
  );
}

export function fetchClubCommentThread(
  postId,
  commentId,
  { sort = "new", descendantsLimit } = {}
) {
  const pid = Number(postId);
  const cid = Number(commentId);
  if (!Number.isFinite(pid) || !Number.isFinite(cid)) {
    throw new Error("Invalid post or comment id");
  }
  const qs = new URLSearchParams({
    sort: String(sort || "new").toLowerCase(),
  });
  const dl = Number(descendantsLimit);
  if (Number.isFinite(dl)) {
    qs.set("descendantsLimit", String(Math.trunc(dl)));
  }
  return apiFetch(`${CLUBS_BASE}/posts/${pid}/comments/${cid}/thread?${qs.toString()}`);
}

export function addClubPostComment(postId, content, options = {}) {
  const id = Number(postId);
  if (!Number.isFinite(id)) throw new Error("Invalid post id");

  const text = content == null ? "" : String(content);
  const parentCommentId = options?.parentCommentId;

  const rawGif = options?.gifUrl ?? options?.gif_url ?? null;
  const gifUrl =
    rawGif != null && String(rawGif).trim() !== "" ? String(rawGif).trim() : null;

  const rawFile = options?.file ?? options?.imageFile ?? null;
  const file = isLikelyFileOrBlob(rawFile) ? rawFile : null;

  if (gifUrl && file) {
    throw new Error("Only one attachment is allowed per comment.");
  }

  /*
   * Architecture (matches backend + avoids fragile multipart for non-binary):
   * - GIF is a provider URL only → send application/json { content, gif_url, parent_comment_id? }.
   * - Uploaded image/GIF file → multipart/form-data with fields: content, parent_comment_id?, file.
   * - Text-only → JSON { content, parent_comment_id? }.
   *
   * Previously GIF+text used multipart; that forced multer + correct multipart Content-Type through
   * fetch/proxy and duplicated failure modes. JSON is the correct transport for URL metadata.
   */
  if (file) {
    const formData = new FormData();
    formData.append("content", text);
    if (parentCommentId != null) formData.append("parent_comment_id", String(parentCommentId));
    formData.append("file", file);

    if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
      let formKeys = [];
      try {
        formKeys =
          typeof formData.keys === "function" ? Array.from(formData.keys()) : [];
      } catch {
        formKeys = [];
      }
      // eslint-disable-next-line no-console
      console.info("[clubsApi addClubPostComment] multipart (file only)", {
        transport: "multipart/form-data",
        url: `${CLUBS_BASE}/posts/${id}/comments`,
        postId: id,
        textLen: text.length,
        hasFile: true,
        hasGif: false,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
        parentCommentId: parentCommentId ?? null,
        formDataKeys: formKeys,
      });
    }

    return apiFetch(`${CLUBS_BASE}/posts/${id}/comments`, {
      method: "POST",
      body: formData,
    });
  }

  const payload = { content: text };
  if (parentCommentId != null) payload.parent_comment_id = parentCommentId;
  if (gifUrl) payload.gif_url = String(gifUrl);

  if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
    // eslint-disable-next-line no-console
    console.info("[clubsApi addClubPostComment] json", {
      transport: "application/json",
      url: `${CLUBS_BASE}/posts/${id}/comments`,
      postId: id,
      textLen: text.length,
      hasGif: Boolean(gifUrl),
      hasFile: false,
      parentCommentId: parentCommentId ?? null,
      jsonPayload: payload,
    });
  }

  return apiFetch(`${CLUBS_BASE}/posts/${id}/comments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function toggleClubCommentLike(postId, commentId) {
  const pid = Number(postId);
  const cid = Number(commentId);
  if (!Number.isFinite(pid) || !Number.isFinite(cid)) {
    throw new Error("Invalid post or comment id");
  }

  return apiFetch(`${CLUBS_BASE}/posts/${pid}/comments/${cid}/like`, {
    method: "POST",
  });
}

export async function updateClubPostComment(postId, commentId, content) {
  const pid = Number(postId);
  const cid = Number(commentId);
  if (!Number.isFinite(pid) || !Number.isFinite(cid)) {
    throw new Error("Invalid post or comment id");
  }

  const path = `${CLUBS_BASE}/posts/${pid}/comments/${cid}`;
  const payload = { content: content == null ? "" : String(content) };
  if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
    // eslint-disable-next-line no-console
    console.info("[clubsApi updateClubPostComment]", {
      method: "PATCH",
      path,
      postId: pid,
      commentId: cid,
      payload,
    });
  }
  const data = await apiFetch(path, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
    // eslint-disable-next-line no-console
    console.info("[clubsApi updateClubPostComment] response", data);
  }
  return data;
}

export async function deleteClubPostComment(postId, commentId) {
  const pid = Number(postId);
  const cid = Number(commentId);
  if (!Number.isFinite(pid) || !Number.isFinite(cid)) {
    throw new Error("Invalid post or comment id");
  }

  const path = `${CLUBS_BASE}/posts/${pid}/comments/${cid}`;
  if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
    // eslint-disable-next-line no-console
    console.info("[clubsApi deleteClubPostComment]", { method: "DELETE", path, postId: pid, commentId: cid });
  }
  const data = await apiFetch(path, {
    method: "DELETE",
  });
  if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
    // eslint-disable-next-line no-console
    console.info("[clubsApi deleteClubPostComment] response", data);
  }
  return data;
}

export function deleteClubPost(postId) {
  const id = Number(postId);
  if (!Number.isFinite(id)) throw new Error("Invalid post id");

  return apiFetch(`${CLUBS_BASE}/posts/${id}`, {
    method: "DELETE",
  });
}

/* -----------------------------
   Club channels (Discord-style; not DMs, not feed)
------------------------------ */

export function fetchClubChannels(slug) {
  if (!slug) throw new Error("Missing slug");
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/channels`);
}

export function createClubChannel(
  slug,
  { name, slug: channelSlug, channel_type, topic } = {}
) {
  if (!slug) throw new Error("Missing slug");
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/channels`, {
    method: "POST",
    body: JSON.stringify({
      name,
      slug: channelSlug,
      channel_type,
      topic,
    }),
  });
}

export function fetchChannelMessages(
  slug,
  channelId,
  { limit = 50, offset = 0 } = {}
) {
  if (!slug) throw new Error("Missing slug");
  const id = Number(channelId);
  if (!Number.isFinite(id)) throw new Error("Invalid channel id");

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  return apiFetch(
    `${CLUBS_BASE}/${encodeURIComponent(slug)}/channels/${id}/messages?${qs.toString()}`
  );
}

export function sendClubChannelMessage(slug, channelId, content) {
  if (!slug) throw new Error("Missing slug");
  const id = Number(channelId);
  if (!Number.isFinite(id)) throw new Error("Invalid channel id");

  return apiFetch(
    `${CLUBS_BASE}/${encodeURIComponent(slug)}/channels/${id}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ content }),
    }
  );
}

/* -----------------------------
   Club events (scheduled activity)
------------------------------ */

export function fetchClubEvents(slug) {
  if (!slug) throw new Error("Missing slug");
  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/events`);
}

export function createClubEvent(
  slug,
  { title, description = null, starts_at, ends_at = null, location_or_link = null } = {}
) {
  if (!slug) throw new Error("Missing slug");
  if (!title || !String(title).trim()) throw new Error("Missing title");
  if (!starts_at) throw new Error("Missing starts_at");

  return apiFetch(`${CLUBS_BASE}/${encodeURIComponent(slug)}/events`, {
    method: "POST",
    body: JSON.stringify({
      title,
      description,
      starts_at,
      ends_at,
      location_or_link,
    }),
  });
}

export function cancelClubEvent(slug, eventId) {
  if (!slug) throw new Error("Missing slug");
  const id = Number(eventId);
  if (!Number.isFinite(id)) throw new Error("Invalid event id");

  return apiFetch(
    `${CLUBS_BASE}/${encodeURIComponent(slug)}/events/${id}/cancel`,
    {
      method: "PATCH",
    }
  );
}
